/**
 * ConfirmCampaignDialog
 *
 * "Potwierdź kampanię" — confirms an ACCEPTED offer (or secures the remaining
 * unsecured nośniki on an already-CONFIRMED one). Renders its own `ModalDialog` shell
 * directly (not `FinalizeConfirmDialog`, which had no body slot for a breakdown
 * table — that wrapper is removed as part of this migration, mirroring
 * `RecordSaleDialog`'s own move off it) so it can show the campaign-confirmation
 * preview as a pre-commit breakdown before the caller can commit (offer-approval /
 * "Approval Action UI in Offer Workspace", action-breakdown).
 *
 * Fetches the breakdown (`useConfirmationPreview`) before the caller can confirm; the
 * confirm control stays disabled while that fetch is in flight or has failed, so a
 * click is never sent with no known breakdown. Joins the preview's
 * `CampaignOutcomeItem[]` rows against the offer's full unit list
 * (`useOfferUnitsAll`) via the shared `OutcomeBreakdownTable` (task 4.1) so a nośnik
 * renders identically here and in the record-sale dialog.
 */
import * as React from 'react';
import { ApiErrorCode } from '@ooh/contracts';
import type { ApproveOfferResponse, Offer } from '@ooh/contracts';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { TFunction } from 'i18next';
import { Button } from '@ooh/ui/components/button';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import { useApproveOffer, useConfirmationPreview } from '../api';
import { useOfferUnitsAll } from '../units-tab/api/queries';
import { OutcomeBreakdownTable } from './outcome-breakdown-table';
import { handleFinalizeMutationError } from './finalize-mutation-error';
import { getConfirmDialogSubtitle } from './confirm-dialog-subtitle';

const CONFIRM_CAMPAIGN_DIALOG_MAX_WIDTH = 'max-w-4xl';

/**
 * Reports the post-approve outcome via toasts, extracted out of `handleApprove` to
 * keep that function's complexity under the lint budget. Partial securing and an
 * unrecognised outcome SHALL NOT present the confirmation itself as failed — both
 * fire as warnings alongside the success toast the caller already showed (offer-
 * approval / "Approval Action UI in Offer Workspace"; action-breakdown / "An
 * unrecognised outcome never presents a committed action as failed"). A queued
 * outcome is excluded from the partial count by construction: it is never
 * 'not_secured'.
 */
function reportApprovalOutcome(approvedOffer: ApproveOfferResponse, t: TFunction<'offers'>): void {
  const notSecuredCount = approvedOffer.securing.results.filter(
    (result) => result.outcome === 'not_secured',
  ).length;
  if (notSecuredCount > 0) {
    toast.warning(t('workspace.header.toasts.approvalPartial', { count: notSecuredCount }));
  }

  const hasUnrecognizedOutcome = approvedOffer.securing.results.some(
    (result) => result.outcome === 'unrecognized',
  );
  if (hasUnrecognizedOutcome) {
    toast.warning(t('workspace.header.toasts.approvalUnrecognizedOutcome'));
  }
}

export function ConfirmCampaignDialog({ offer, onClose }: { offer: Offer; onClose: () => void }) {
  const { t } = useTranslation('offers');
  const approveOffer = useApproveOffer();
  // Dialog only mounts while open, so both queries are always enabled here (same
  // convention as RecordSaleDialog).
  const preview = useConfirmationPreview(String(offer.id));
  const allUnits = useOfferUnitsAll(offer.id, { enabled: true });

  const items = React.useMemo(() => preview.data ?? [], [preview.data]);
  const units = allUnits.data ?? [];
  const isLoading = preview.isLoading || allUnits.isLoading;
  const isError = preview.isError;
  const resolved = !isLoading && !isError;
  // A breakdown with nothing but already-secured entries (including no entries at
  // all — `[].every(...)` is vacuously true) has nothing left for the action to
  // change (offer-approval / "The dialog... states plainly that there is nothing
  // left to secure").
  const nothingToSecure = resolved && items.every((item) => item.outcome === 'already_secured');
  const showBreakdown = resolved && !nothingToSecure;
  const confirmDisabled = approveOffer.isPending || isLoading || isError;

  async function handleApprove() {
    try {
      const approvedOffer = await approveOffer.mutateAsync({ id: String(offer.id) });
      onClose();
      toast.success(
        t('workspace.header.toasts.approved', {
          campaignNumber: approvedOffer.campaignNumber ?? '',
        }),
      );
      reportApprovalOutcome(approvedOffer, t);
    } catch (error) {
      handleFinalizeMutationError(error, t, {
        conflictCode: ApiErrorCode.OFFER_NOT_ACCEPTED,
        conflictToastKey: 'workspace.header.toasts.approvalConflict',
        forbiddenToastKey: 'workspace.header.toasts.approvalForbidden',
        genericToastKey: 'workspace.header.toasts.approvalError',
      });
    }
  }

  return (
    <ModalDialog
      testId="confirm-campaign-dialog"
      maxWidth={CONFIRM_CAMPAIGN_DIALOG_MAX_WIDTH}
      closeLabel={t('workspace.header.confirmDialog.close')}
      onClose={onClose}
      header={
        <div>
          <h2 className="text-base font-semibold">{t('workspace.header.confirmDialog.title')}</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {getConfirmDialogSubtitle(t, { isLoading, isError, nothingToSecure })}
          </p>
        </div>
      }
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('workspace.header.confirmDialog.cancel')}
          </Button>
          <Button
            type="button"
            variant="success"
            disabled={confirmDisabled}
            onClick={() => void handleApprove()}
          >
            {t('workspace.header.confirmDialog.confirm')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {isError ? (
          <Button type="button" variant="outline" size="sm" onClick={() => preview.refetch()}>
            {t('workspace.header.confirmDialog.retry')}
          </Button>
        ) : null}

        {showBreakdown ? <OutcomeBreakdownTable t={t} items={items} units={units} /> : null}

        <p className="text-sm text-muted-foreground">
          {t('workspace.header.confirmDialog.queueNote')}
        </p>
        <p className="text-sm text-muted-foreground">
          {t('workspace.header.confirmDialog.helpText')}
        </p>
      </div>
    </ModalDialog>
  );
}
